import React, { useEffect, useState } from 'react';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import AiWorkflowProgress from '~/components/Common/AiWorkflowProgress';
import JsonBlock from '~/components/Common/Blocks/JsonBlock';

import { usePromoVoucherAiWorkflow } from '~/hooks/apis/promo/usePromoVoucherAiWorkflow';
import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';

import {
  type AiCodeKind,
  type AiCodeMessage,
  type AiCodeOperation,
  type AiCodeProgressEvent,
} from '~/services/PromoVoucherAiService';

import PromoAiQueryExamples from './PromoAiQueryExamples';
import PromoFeature from './PromoFeature';

type Props = {
  open: boolean;
  kind: AiCodeKind;
  onClose: () => void;
  onSaved?: () => void;
  initialCodeName?: string;
  initialRequest?: string;
  initialOperation?: AiCodeOperation;
};

export default function PromoVoucherAiDialog({
  open,
  kind,
  onClose,
  onSaved,
  initialCodeName = '',
  initialRequest = '',
  initialOperation = 'create',
}: Props) {
  const { promoBrand, hasValidPromoBrand } = useCurrentPromoBrand();
  const { generate, save } = usePromoVoucherAiWorkflow();
  const resetGenerate = generate.reset;
  const resetSave = save.reset;
  const [operation, setOperation] = useState<AiCodeOperation>('create');
  const [codeName, setCodeName] = useState('');
  const [request, setRequest] = useState('');
  const [conversation, setConversation] = useState<Array<AiCodeMessage>>([]);
  const [previousProposal, setPreviousProposal] = useState<unknown>(null);
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(false);
  const [writeAttempted, setWriteAttempted] = useState(false);
  const [progress, setProgress] = useState<Array<AiCodeProgressEvent>>([]);

  useEffect(() => {
    if (open) {
      setOperation(initialOperation);
      setCodeName(initialCodeName);
      setRequest(initialRequest);
      setConversation([]);
      setPreviousProposal(null);
      setAcknowledgedWarnings(false);
      setWriteAttempted(false);
      setProgress([]);
      resetGenerate();
      resetSave();
    }
  }, [open, resetGenerate, resetSave, initialCodeName, initialOperation, initialRequest]);

  const clearReview = () => {
    setAcknowledgedWarnings(false);
    setWriteAttempted(false);
    setProgress([]);
    resetGenerate();
    resetSave();
  };

  const recordProgress = (event: AiCodeProgressEvent) => {
    setProgress((current) => {
      if (event.status === 'done' && current[current.length - 1]?.step === event.step) {
        return [...current.slice(0, -1), event];
      }
      return [...current, event];
    });
  };

  const markProgressFailed = () => {
    setProgress((current) => {
      const last = current[current.length - 1];
      return last?.status === 'running' ? [...current.slice(0, -1), { ...last, status: 'failed' as const }] : current;
    });
  };

  const handleGenerate = () => {
    const nextConversation: Array<AiCodeMessage> = [...conversation, { role: 'user', content: request.trim() }];
    clearReview();
    generate.mutate(
      {
        context: {
          kind,
          operation,
          codeName: codeName.trim(),
          brand: promoBrand,
          request: request.trim(),
          conversation: nextConversation,
          previousProposal,
        },
        onProgress: recordProgress,
      },
      {
        onSuccess: (result) => {
          if (result.type === 'proposal') {
            setPreviousProposal(result.payload);
          }
          setConversation([
            ...nextConversation,
            {
              role: 'assistant',
              content: result.type === 'question' ? [result.summary, ...result.questions].join('\n') : result.summary,
            },
          ]);
          setRequest('');
        },
        onError: markProgressFailed,
      },
    );
  };

  const handleSave = async () => {
    if (generate.data?.type !== 'proposal') {
      return;
    }
    setWriteAttempted(true);
    try {
      await save.mutateAsync({ proposal: generate.data, onProgress: recordProgress });
      onSaved?.();
    } catch {
      markProgressFailed();
      // The mutation error is shown below. Never retry a write automatically.
    }
  };

  const proposal = generate.data?.type === 'proposal' ? generate.data : null;
  const question = generate.data?.type === 'question' ? generate.data : null;
  const hasWarnings = (proposal?.warnings.length ?? 0) > 0;
  const canSave = proposal && !writeAttempted && proposal.errors.length === 0 && (!hasWarnings || acknowledgedWarnings);

  return (
    <Dialog open={open} onClose={save.isPending ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>AI {kind === 'promo' ? 'promo code' : 'voucher'} workflow</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <PromoFeature featureKey="ai-promo-tools-access" presentation="raw">
            <Alert severity="info">
              The AI prepares a proposal and retries voucher validation up to three times. Review the exact changes and
              approve the write yourself.
              <br />
              Brand: {promoBrand}.
            </Alert>
          </PromoFeature>
          {!hasValidPromoBrand && <Alert severity="error">This tenant has no configured promo brand.</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Operation"
              value={operation}
              onChange={(event) => {
                setOperation(event.target.value as AiCodeOperation);
                setConversation([]);
                setPreviousProposal(null);
                clearReview();
              }}
              sx={{ minWidth: 150 }}
              disabled={generate.isPending || save.isPending}
            >
              <MenuItem value="create">Create</MenuItem>
              <MenuItem value="update">Update</MenuItem>
            </TextField>
          </Stack>
          {conversation.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1} aria-label="AI conversation">
                {conversation.map((message, index) => (
                  <Box key={index}>
                    <Typography variant="caption" color="text.secondary">
                      {message.role === 'user' ? 'You' : 'AI'}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {message.content}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              label={question ? 'Reply to the AI' : conversation.length > 0 ? 'Continue the conversation' : 'Query'}
              value={request}
              onChange={(event) => {
                setRequest(event.target.value);
                if (generate.data?.type !== 'question') {
                  clearReview();
                }
              }}
              required
              multiline
              minRows={3}
              fullWidth
              disabled={generate.isPending || save.isPending}
              helperText="Include the reward or discount, eligible products and regions, limits, and dates where relevant."
            />
            {conversation.length === 0 && (
              <PromoAiQueryExamples
                kind={kind}
                disabled={generate.isPending || save.isPending}
                onSelect={(query) => {
                  setRequest(query);
                  clearReview();
                }}
              />
            )}
            <TextField
              label={kind === 'promo' ? 'Promo code name' : 'Voucher name'}
              value={codeName}
              onChange={(event) => {
                setCodeName(event.target.value);
                setConversation([]);
                setPreviousProposal(null);
                clearReview();
              }}
              required
              sx={{ width: { xs: '100%', sm: '30%' }, flexShrink: 0 }}
              disabled={generate.isPending || save.isPending}
            />
          </Stack>
          <Box>
            <Button
              variant="contained"
              startIcon={generate.isPending ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
              disabled={
                !hasValidPromoBrand || !codeName.trim() || !request.trim() || generate.isPending || save.isPending
              }
              onClick={handleGenerate}
            >
              {generate.isPending ? 'Preparing...' : conversation.length > 0 ? 'Send and prepare' : 'Prepare proposal'}
            </Button>
          </Box>
          {progress.length > 0 && (
            <AiWorkflowProgress
              events={progress}
              includeSave
              validationPassed={
                proposal ? proposal.errors.length === 0 && proposal.warnings.length === 0 : question ? false : undefined
              }
            />
          )}
          {generate.isError && (
            <Alert severity="error">
              {generate.error instanceof Error ? generate.error.message : 'Unable to prepare proposal.'}
            </Alert>
          )}
          {question && (
            <Alert severity="info">
              <Typography variant="subtitle2">The AI needs more detail</Typography>
              {question.questions.map((item) => (
                <Typography key={item} variant="body2">
                  {item}
                </Typography>
              ))}
              Reply above to continue.
            </Alert>
          )}
          {proposal && (
            <Stack spacing={2}>
              <Typography variant="h6">Review proposal</Typography>
              <Typography>{proposal.summary}</Typography>
              {kind === 'voucher' && (hasWarnings || proposal.errors.length > 0) && (
                <Alert severity="error">
                  Voucher validation still has failures after {proposal.validationAttempts} attempts. Review the
                  validation results below. How should these issues be resolved? Update “Describe the change” with the
                  missing details or corrections, then prepare a new proposal.
                </Alert>
              )}
              {kind === 'promo' && operation === 'update' && (
                <Alert severity="warning">
                  Promo updates have no pre-save definition check. Review the differences carefully.
                </Alert>
              )}
              <Typography variant="subtitle1">Changed fields</Typography>
              {proposal.changes.length === 0 ? (
                <Alert severity="warning">No changes were proposed.</Alert>
              ) : (
                <JsonBlock value={proposal.changes} />
              )}
              <Typography variant="subtitle1">Exact write payload</Typography>
              <JsonBlock value={proposal.payload} />
              {proposal.preview && (
                <>
                  <Typography variant="subtitle1">Pre-save check or preview</Typography>
                  <JsonBlock value={proposal.preview} />
                </>
              )}
              {hasWarnings && (
                <Alert severity="warning">
                  <Typography variant="subtitle2">Warnings</Typography>
                  {proposal.warnings.map((warning, index) => (
                    <Typography key={`${index}-${warning}`}>{warning}</Typography>
                  ))}
                </Alert>
              )}
              {proposal.errors.length > 0 && (
                <Alert severity="error">
                  <Typography variant="subtitle2">Pre-save errors</Typography>
                  {proposal.errors.map((error, index) => (
                    <Typography key={`${index}-${error}`}>{error}</Typography>
                  ))}
                </Alert>
              )}
              {hasWarnings && kind !== 'voucher' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acknowledgedWarnings}
                      onChange={(event) => setAcknowledgedWarnings(event.target.checked)}
                    />
                  }
                  label="I reviewed the warnings"
                />
              )}
            </Stack>
          )}
          {save.isError && (
            <Alert severity="error">
              {save.error instanceof Error ? save.error.message : 'Unable to save.'} Generate a fresh proposal before
              another write attempt.
            </Alert>
          )}
          {save.isSuccess && (
            <Alert severity={save.data.mismatches.length > 0 ? 'warning' : 'success'}>
              Saved.{' '}
              {save.data.mismatches.length > 0
                ? `Readback differs for: ${save.data.mismatches.join(', ')}.`
                : 'Readback matched the reviewed fields.'}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Code check result
              </Typography>
              <JsonBlock value={save.data.check} />
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={save.isPending}>
          Close
        </Button>
        {proposal && !save.isSuccess && (
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={!canSave || proposal.changes.length === 0 || (kind === 'voucher' && hasWarnings)}
          >
            {save.isPending ? 'Saving...' : `Approve and ${operation}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
